import { useMemo, useState, useEffect, Fragment } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Stack,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  return dayjs(isoString).format('DD MMM YYYY, HH:mm');
};

const formatHoursMinutes = (totalSeconds, t) => {
  if (!totalSeconds || totalSeconds < 0) return t('dashboardAsset.zeroHoursZeroMinutes', '0 Hours 0 Minutes');
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours} ${t('dashboardAsset.hours', 'Hours')} ${minutes} ${t('dashboardAsset.minutes', 'Minutes')}`;
};

const ExpandableRow = ({ row, index, showDriver, t }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      {/* Baris Utama (Summary Kendaraan) */}
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor: open ? 'rgba(72, 93, 154, 0.04)' : 'transparent',
          transition: 'background-color 0.3s'
        }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: open ? '#485D9A' : 'text.secondary' }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="center">{index}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{showDriver ? row.driverName : row.vehicleNumber}</TableCell>
        <TableCell align="center">{row.retase}</TableCell>
        <TableCell align="center">{row.avgRetase}</TableCell>
        <TableCell align="center">{row.totalHours}</TableCell>
        <TableCell align="center">{row.hoursPerDay}</TableCell>
      </TableRow>

      {/* Baris Expand (Detail Trip & Routes) */}
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, borderBottom: open ? '1px solid rgba(224, 224, 224, 1)' : 'none' }}
          colSpan={7}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2, p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px dashed #cfd8dc' }}>
              <Typography variant="subtitle2" fontWeight="700" color="#485D9A" gutterBottom display="flex" alignItems="center" gap={1}>
                <TimelineIcon fontSize="small" /> {t('dashboardAsset.tripDetails', 'Trip Details')}
              </Typography>

              <Table size="small" sx={{ mt: 1, '& .MuiTableCell-root': { borderColor: '#eceff1' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#485D9A' }}>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      No
                    </TableCell>
                    {showDriver && (
                      <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('dashboardAsset.vehicle', 'Vehicle')}</TableCell>
                    )}
                    {!showDriver && (
                      <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('dashboardAsset.driverName', 'Driver Name')}</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('dashboardAsset.startTime', 'Start Time')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('dashboardAsset.endTime', 'End Time')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('dashboardAsset.duration', 'Duration')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>{t('dashboardAsset.routesPoints', 'Routes / Points')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.trips.map((trip, idx) => {
                    const routesOrVisits = trip.routes || trip.visits || [];
                    const driverName = trip.driver_name || '-';
                    const vehicleNum = trip.license_plate || trip.vehicle_name || '-';
                    return (
                      <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                          {idx + 1}
                        </TableCell>
                        {showDriver && <TableCell sx={{ fontSize: '0.8rem' }}>{vehicleNum}</TableCell>}
                        {!showDriver && <TableCell sx={{ fontSize: '0.8rem' }}>{driverName}</TableCell>}
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDateTime(trip.in_time)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDateTime(trip.out_time)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatHoursMinutes(trip.duration, t)}</TableCell>
                        <TableCell>
                          {routesOrVisits.length > 0 ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
                              {routesOrVisits.map((visit, vIdx) => (
                                <Chip
                                  key={vIdx}
                                  size="medium"
                                  label={`${visit.sequence_number || vIdx + 1}. ${visit.spatial_name || visit.name || 'Unknown'}`}
                                  sx={{
                                    fontSize: '0.7rem',
                                    bgcolor: '#fff',
                                    border: '1px solid #d0d7de',
                                    color: '#334155',
                                    fontWeight: 500
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              {t('dashboardAsset.noRoutesAvailable', 'No routes available')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default function RitaseDialog({ open, onClose, data, showDriver = false }) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setPage(0);
    }
  }, [open]);

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const grouped = {};

    data.forEach((trip) => {
      const vId = trip.vehicle_id;
      const plate = trip.license_plate || trip.vehicle_name || `Unknown-${vId}`;
      const dId = trip.driver_id || `unknown-driver-${vId}`;
      const dName = trip.driver_name || '-';

      const key = showDriver ? dId : vId;

      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          vehicleNumber: plate,
          driverName: dName,
          retase: 0,
          totalSeconds: 0,
          activeDays: new Set(),
          trips: []
        };
      }

      grouped[key].retase += trip.ritase_count || 1;
      grouped[key].trips.push(trip);

      if (trip.in_time && trip.in_time !== '-') {
        grouped[key].activeDays.add(dayjs(trip.in_time).format('YYYY-MM-DD'));
      }

      let durationSec = trip.duration || 0;
      if (durationSec === 0 && trip.in_time && trip.out_time && trip.in_time !== '-' && trip.out_time !== '-') {
        const start = dayjs(trip.in_time);
        const end = dayjs(trip.out_time);
        durationSec = end.diff(start, 'second');
      }

      if (durationSec > 0) {
        grouped[key].totalSeconds += durationSec;
      }
    });

    return Object.values(grouped).map((v, index) => {
      const daysCount = v.activeDays.size > 0 ? v.activeDays.size : 1;
      const formattedTotal = formatHoursMinutes(v.totalSeconds, t);
      const avgSecondsPerDay = v.totalSeconds / daysCount;
      const formattedAvgPerDay = formatHoursMinutes(avgSecondsPerDay, t);

      return {
        id: v.id || `ritase-${index}`,
        vehicleNumber: v.vehicleNumber,
        driverName: v.driverName,
        retase: v.retase,
        avgRetase: (v.retase / daysCount).toFixed(1),
        totalHours: formattedTotal,
        hoursPerDay: formattedAvgPerDay,
        trips: v.trips
      };
    });
  }, [data, showDriver, t]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return processedData;
    const lower = searchTerm.toLowerCase();
    return processedData.filter((r) =>
      showDriver ? r.driverName.toLowerCase().includes(lower) : r.vehicleNumber.toLowerCase().includes(lower)
    );
  }, [processedData, searchTerm, showDriver]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {t('dashboardAsset.ritaseDetailsToday', 'Ritase Details Today')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2, bgcolor: '#f4f6f8' }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder={
              showDriver
                ? t('dashboardAsset.searchByDriverName', 'Search by Driver Name...')
                : t('dashboardAsset.searchByPlate', 'Search by Plate...')
            }
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table aria-label="achievement expandable table">
            <TableHead>
              <TableRow sx={{ bgcolor: '#485D9A' }}>
                <TableCell />
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  No
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  {showDriver ? t('dashboardAsset.driverName', 'Driver Name') : t('dashboardAsset.vehicleNumber', 'Vehicle Number')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  {t('dashboardAsset.ritase', 'Ritase')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  {t('dashboardAsset.avgRitaseDay', 'Average Ritase/Day')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  {t('dashboardAsset.totalWorkingHours', 'Total Working Hours')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  {t('dashboardAsset.workingHoursDay', 'Working Hours/Day')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row, idx) => (
                  <ExpandableRow key={row.id} row={row} index={page * rowsPerPage + idx + 1} showDriver={showDriver} t={t} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    {t('dashboardAsset.noDataAvailable', 'No data available for the selected time range.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
